import * as React from "react"

import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cva, type VariantProps } from "class-variance-authority"
import { Lightbulb } from "lucide-react"

import { cn } from "../../cn"

/**
 * House timing for every tooltip. The app's single provider owns these and
 * nothing below it sets a delay of its own, because `skipDelayDuration` only
 * means anything across a group of tooltips sharing one provider.
 */
const TOOLTIP_DELAY_DURATION = 100
const TOOLTIP_SKIP_DELAY_DURATION = 100

/**
 * House placement for every tooltip: above the thing it labels. Radix already
 * resolves to the top side and leaves `avoidCollisions` on, so a tooltip that
 * would run off the viewport flips to the bottom on its own and needs no
 * positioning logic here. Naming the default anyway keeps the rule readable in
 * our own code rather than inherited from the library, so a call site that
 * wants something else has to say so.
 */
const TOOLTIP_SIDE = "top"
/** Gap between the tooltip and its trigger. */
const TOOLTIP_SIDE_OFFSET = 4
/**
 * Breathing room a flipped or shifted tooltip keeps from the viewport edge.
 * Radix leaves this at 0, which lets a tooltip sit flush against the edge it
 * just avoided.
 */
const TOOLTIP_COLLISION_PADDING = 8

/**
 * Whether a `TooltipProvider` is already above us. Radix throws without one,
 * which would make every tree holding an icon button — a test, a story, a
 * fragment rendered on its own — responsible for knowing that the button has a
 * tooltip inside it. `Tooltip` supplies its own when this is false, so the app's
 * single provider still owns timing wherever it is mounted and nowhere grows a
 * second delay group.
 */
const TooltipProviderPresence = React.createContext(false)

const TooltipProvider = ({
  delayDuration = TOOLTIP_DELAY_DURATION,
  skipDelayDuration = TOOLTIP_SKIP_DELAY_DURATION,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <TooltipProviderPresence.Provider value>
    <TooltipPrimitive.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    >
      {children}
    </TooltipPrimitive.Provider>
  </TooltipProviderPresence.Provider>
)

const tooltipContentVariants = cva(
  [
    // Tooltips run on the inverse of the page's theme: dark panel with light
    // text on a light page, and the reverse on a dark one. That is what keeps
    // a tooltip from reading as one more panel among the surfaces it floats
    // over. `coval-theme-invert` flips the whole token set for the subtree, so
    // caller-supplied content — muted text, status colours, nested chips —
    // lands on the inverted surface with its contrast intact.
    "coval-theme-invert z-50 overflow-hidden rounded-md text-sm text-coval-text",
    "border border-coval-border bg-surface-default",
    "animate-in fade-in-0 zoom-in-95",
    "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
    "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  ],
  {
    variants: {
      variant: {
        // A label for the thing under the pointer: tight, and lifted just
        // enough to separate it from what it labels.
        default: "px-2 py-1 shadow-md",
        // Guidance the user is meant to read and act on. Roomier and more
        // strongly lifted, so several lines of advice read as a card instead
        // of a smudge, and led by a lightbulb so its purpose is clear before
        // the words are.
        tip: "max-w-xs px-4 py-3.5 shadow-lg",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

export interface TooltipProps extends VariantProps<
  typeof tooltipContentVariants
> {
  content: React.ReactNode
  children: React.ReactElement
  /**
   * Which side of the trigger to prefer. Defaults to `"top"`, which is the
   * house rule; Radix flips to the opposite side by itself when the preferred
   * one would leave the viewport, so this is for deliberate exceptions —
   * `"left"` / `"right"` in a narrow rail, or `"bottom"` under a sticky header
   * that Radix cannot see — not for collision handling.
   */
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  /**
   * Holds the tooltip open on top of the usual hover and focus behavior, for
   * callers that also use it as action feedback (e.g. a copy confirmation that
   * has to outlive the click, which dismisses the hover tooltip).
   */
  forceOpen?: boolean
  /**
   * Whether pointer and focus interactions can open the tooltip. Disable this
   * for feedback-only tooltips attached to a large trigger surface.
   */
  openOnInteraction?: boolean
  /**
   * Extra classes for the tooltip content. Merged after the defaults, so it can
   * override them (e.g. raising the z-index when the tooltip is opened from
   * inside a high-stacking overlay that would otherwise occlude it).
   */
  className?: string
  /** Data attributes forwarded to the portaled tooltip content. */
  contentDataAttributes?: {
    [attributeName: `data-${string}`]: string | undefined
  }
}

const Tooltip = ({
  content,
  children,
  side = TOOLTIP_SIDE,
  align,
  variant,
  forceOpen = false,
  openOnInteraction = true,
  className,
  contentDataAttributes,
}: TooltipProps) => {
  const [hoverOpen, setHoverOpen] = React.useState(false)
  const hasProvider = React.useContext(TooltipProviderPresence)

  const tooltip = (
    <TooltipPrimitive.Root
      open={forceOpen || (openOnInteraction && hoverOpen)}
      onOpenChange={openOnInteraction ? setHoverOpen : undefined}
    >
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          {...contentDataAttributes}
          side={side}
          align={align}
          sideOffset={TOOLTIP_SIDE_OFFSET}
          collisionPadding={TOOLTIP_COLLISION_PADDING}
          className={cn(tooltipContentVariants({ variant }), className)}
        >
          {variant === "tip" ? (
            <div className="flex items-start gap-3">
              <Lightbulb
                className="mt-0.5 size-4 shrink-0 text-coval-warning-text"
                aria-hidden="true"
              />
              <div className="min-w-0 leading-5">{content}</div>
            </div>
          ) : (
            content
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )

  return hasProvider ? tooltip : <TooltipProvider>{tooltip}</TooltipProvider>
}

Tooltip.displayName = "Tooltip"

export { Tooltip, TooltipProvider, tooltipContentVariants }
